import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submit } from 'redux-form';
import { cloneDeep, find, includes, isEmpty, isEqual, isNil } from 'lodash';
import { ButtonGroup, Chip, Tooltip } from '@mui/material';
import { Done as DoneIcon, FilterList } from '@mui/icons-material';

import SearchPresetDialog from '../../../screens/Dashboard/components/RapidSearch/components/SearchPresetDialog';
import SearchPresetDeleteDialog from '../../../screens/Dashboard/components/RapidSearch/components/SearchPresetDeleteDialog';
import { loadPlazasList } from '../../../screens/PlazaList/components/PlazasDictionary/actions';

import { dictionariesTypesEnum } from '../../constants/dictionaries';
import { permissions } from '../../constants/permissions';
import { changeFilterQuery, closeSSP, deselectAll, onFilterChange, setAdvancedSearchQuery } from '../SSPList/actions';
import { Dialog } from '../Dialog/Dialog';
import Button from '../Button/Button';

import { emptyQuery } from './QueryBuilder/constants';
import { SearchQueryExpression } from './SearchQueryExpression';

import {
  createAdvancedSearchPreset,
  deleteAdvancedSearchPreset,
  loadAdvancedSearchPresets,
  setActiveAdvancedSearchPreset,
  setSelectedTabAdvancedSearch,
  updateAdvancedSearchPreset,
} from './actions';

import './_styles.scss';
import { loadAllDictionaries } from '../../../screens/AdminTools/components/Dictionary/actions';
import { loadDictionary } from '../../../screens/AdminTools/components/CustomerDictionary/actions';
import TooltipWrapper from '../Form/TooltipWrapper';
import { expressSearchQuery } from './SearchQueryExpression/utils';
import { isOnlyMobile } from '../../services/isMobile';
import ConfirmationDialog from '../Form/ConfirmationDialog';
import { Tab, Tabs } from '../Tabs/Tabs';
import { chargerConfigurationStatus, getConfigurationConfig } from '../../../screens/ChargersList/constants';
import { selectConfigurationNodeAvailability, selectPlazaAvailabilityMode, selectTechInfo, selectUiLabels } from '../appConfig/selector';
import QueryBuilder from './QueryBuilder/QueryBuilder';
import { selectActivePresetId, selectAdvancedSearchPresets, selectCurrentSearchTab } from './selectors';

import {
  selectCustomersDictionary,
  selectPlazasDictionary,
  selectStoredDictionary,
} from '../../../screens/AdminTools/components/Dictionary/selectors';
import { selectUserPermissions } from '../../../screens/Login/selectors';
import { TABS } from './utils';
import { selectAppliedFilter, selectIsExistSimpleFilter, selectIsFilterApplied, selectSsplistsQuery } from '../SSPList/selectors';

const filterIconStyle = { color: 'rgb(33, 33, 33)', backgroundColor: 'white', minWidth: '40px' };
const CHARGER_LIST_BY_CONFIG_PARAMS = 'chargersListByConfigParams';
const dialogActionsStyle = {
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  position: 'relative',
  alignItems: 'normal',
};

const paperStyle = {
  width: 1110,
};

const AdvancedSearch = ({
  onToggle,
  enabled,
  onApply,
  excludedFields = [],
  isShowAdvancedSearch,
  buttonsStyle,
  getFields,
  filterButtonLabel = 'Advanced Filter',
  includedFields,
  id,
}) => {
  const dispatch = useDispatch();

  const [createPresetDialogOpened, setCreatePresetDialogOpened] = useState(false);
  const [deletePresetDialogOpened, setDeletePresetDialogOpened] = useState(false);
  const [switchTabDialogOpened, setSwitchTabDialogOpened] = useState(false);
  const [closeDialogOpened, setCloseDialogOpened] = useState(false);
  const [presetToDeleteId, setPresetToDeleteId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isFilterClearedFromPopup, setIsFilterClearedFromPopup] = useState(false);
  const [clickedTab, setClickedTab] = useState(null);
  const [configurationNodes, setConfigurationNodes] = useState([]);

  const isNodeConfigurationParametersAvailable = useSelector(selectConfigurationNodeAvailability);
  const selectedTab = useSelector(selectCurrentSearchTab(id));
  const query = useSelector(selectSsplistsQuery(id));
  const existSimpleFilter = useSelector(selectIsExistSimpleFilter(id));
  const appliedQuery = useSelector(selectAppliedFilter(id));
  const isFilterApplied = useSelector(selectIsFilterApplied(id));
  const presets = useSelector(selectAdvancedSearchPresets);
  const activePresetId = useSelector(selectActivePresetId);
  const dictionaries = useSelector(selectStoredDictionary);
  const plazas = useSelector(selectPlazasDictionary);
  const customers = useSelector(selectCustomersDictionary);
  const uiLabels = useSelector(selectUiLabels);
  const plazaAvailabilityMode = useSelector(selectPlazaAvailabilityMode);
  const techInfo = useSelector(selectTechInfo);
  const userPermissions = useSelector(selectUserPermissions);

  const isSupervisorOrFullAccess = userPermissions.includes(permissions.SUPERVISOR) || userPermissions.includes(permissions.FULL_ACCESS);
  const isFilterWasNotChanged = isEqual(appliedQuery, query);
  const filterId = selectedTab === TABS.CONFIGURATION ? `${CHARGER_LIST_BY_CONFIG_PARAMS}-${id}` : id;
  const isHiddenChargerConfiguration =
    !isNodeConfigurationParametersAvailable ||
    !(isSupervisorOrFullAccess || userPermissions.includes(permissions.VIEW_CHARGER_CONFIGURATION));

  const toggleQueryBuilder = () => setIsOpen(!isOpen);

  const handleApply = () => {
    if (!isFilterApplied) {
      onToggle(null, true);
    }

    onApply(query);

    toggleQueryBuilder();
    setIsOpen(false);

    dispatch(deselectAll(id));
    dispatch(setAdvancedSearchQuery(id, query));
  };

  const onKeyDown = (e) => {
    if (isOpen && e.ctrlKey && e.code === 'Enter') {
      handleApply();
    }
  };

  const onChange = (newQuery) => dispatch(changeFilterQuery(id, newQuery));

  const onClear = () => {
    if (enabled && isOpen) {
      setIsFilterClearedFromPopup(true);
    } else if (enabled) {
      onToggle(null, false);
    }

    const newQuery = cloneDeep(emptyQuery);

    dispatch(changeFilterQuery(id, newQuery));
    dispatch(deselectAll(id));
    dispatch(setAdvancedSearchQuery(id, newQuery));
    dispatch(setActiveAdvancedSearchPreset(null));
    dispatch(onFilterChange(id, { filter_by: null }));

    onApply(newQuery);
  };

  const onClose = () => {
    if (!isEqual(query, appliedQuery)) {
      setCloseDialogOpened(true);

      return;
    }

    if (isFilterClearedFromPopup) {
      onClear();
      onToggle(null, false);
      setIsFilterClearedFromPopup(false);
    }

    toggleQueryBuilder();
    dispatch(closeSSP(id));
  };

  const openCreatePresetDialog = () => {
    if (activePresetId === null) {
      setCreatePresetDialogOpened(true);
    } else {
      const { name, description } = find(presets, { uuid: activePresetId });

      const preset = {
        uuid: activePresetId,
        name,
        description,
        query: cloneDeep(query),
      };

      dispatch(updateAdvancedSearchPreset(preset, filterId));
    }
  };

  const closeCreatePresetDialog = () => setCreatePresetDialogOpened(false);

  const openDeletePresetDialog = (uuid) => {
    setDeletePresetDialogOpened(true);
    setPresetToDeleteId(uuid);
  };

  const closeDeletePresetDialog = () => {
    setDeletePresetDialogOpened(false);
    setPresetToDeleteId(null);
  };

  const deletePreset = () => {
    if (presetToDeleteId === activePresetId) {
      dispatch(setActiveAdvancedSearchPreset(null));
    }

    dispatch(deleteAdvancedSearchPreset(presetToDeleteId));
    closeDeletePresetDialog();
  };

  const createPreset = ({ name, description }) => {
    const preset = {
      name,
      description,
      query: cloneDeep(query),
    };

    dispatch(createAdvancedSearchPreset(preset, filterId));
    closeCreatePresetDialog();
  };

  const applyPreset = (uuid) => {
    const { query } = find(presets, { uuid });

    dispatch(setActiveAdvancedSearchPreset(uuid));
    dispatch(changeFilterQuery(id, query));
  };

  const filterOpen = () => setIsOpen(true);

  const renderPresets = () => {
    if (presets.length > 0) {
      const mappedPresets = presets.map(({ uuid, name }) => {
        const icon = activePresetId === uuid ? <DoneIcon /> : undefined;

        return (
          <Chip
            key={uuid}
            label={name}
            value={uuid}
            icon={icon}
            onClick={() => applyPreset(uuid)}
            onDelete={() => openDeletePresetDialog(uuid)}
          />
        );
      });

      return <div className="advanced-search-saved-filters">{mappedPresets}</div>;
    }

    return null;
  };

  const renderDialogFooter = () => {
    const actions = [
      <Button key="create-btn" color="primary" onClick={openCreatePresetDialog}>
        Save Filter
      </Button>,
      <Button key="clear-btn" color="primary" variant="text" onClick={onClear}>
        Clear
      </Button>,
      <Button key="close-btn" color="primary" variant="text" onClick={onClose}>
        Close
      </Button>,
      <Tooltip key="apply-btn-tooltip" title="Ctrl + Enter" placement="top" enterDelay={400}>
        <div key="apply-btn-div" className="advanced-search-apply-filter-btn">
          <Button key="apply-btn" color="primary" disabled={isFilterWasNotChanged} onClick={handleApply}>
            Apply
          </Button>
        </div>
      </Tooltip>,
    ];

    return <div className="advanced-search-actions-wrapper">{actions}</div>;
  };

  const addExcludedFieldsBySettings = () => {
    const newExcludedFields = cloneDeep(excludedFields);

    if (isNil(plazaAvailabilityMode)) newExcludedFields.push('plaza_uuid');
    if (!isNil(techInfo) && includes(techInfo, 'chademo')) newExcludedFields.push('chademo_cable');
    if (isHiddenChargerConfiguration) {
      newExcludedFields.push(chargerConfigurationStatus);
    }

    return newExcludedFields;
  };

  const closeWarningDialog = () => setIsWarningOpen(false);

  const openWarningDialog = () => setIsWarningOpen(true);

  const onWarningSubmit = () => {
    filterOpen();
    closeWarningDialog();
  };

  const changeTabHandler = (tab) => {
    setClickedTab(tab);

    if (!isEqual(appliedQuery, query)) {
      setCloseDialogOpened(true);

      return;
    }

    if (!isEqual(appliedQuery, emptyQuery)) {
      setSwitchTabDialogOpened(true);

      return;
    }

    dispatch(setSelectedTabAdvancedSearch(tab, id));
  };

  const proceedSwitchTabHandler = () => {
    onClear();
    dispatch(setSelectedTabAdvancedSearch(clickedTab, id));
    setSwitchTabDialogOpened(false);
  };

  const proceedCloseDialogHandler = () => {
    if (!isEqual(selectedTab, clickedTab) && !isNil(clickedTab)) {
      onClear();
      setCloseDialogOpened(false);
      dispatch(setSelectedTabAdvancedSearch(clickedTab, id));

      return;
    }

    if (isFilterClearedFromPopup) {
      onClear();
      onToggle(null, false);
      setIsFilterClearedFromPopup(false);
    }

    toggleQueryBuilder();
    dispatch(closeSSP(id));
    setCloseDialogOpened(false);
  };

  const onFilterButtonClick = () => {
    if (!isFilterApplied && existSimpleFilter) {
      openWarningDialog();
    } else {
      filterOpen();
    }
  };

  const currentQuery = isEmpty(query) ? emptyQuery : query;
  const newExcludedFields = addExcludedFieldsBySettings();
  const config = {
    ...dictionaries,
    uiLabels,
    excludedFields: newExcludedFields,
    plazas,
    customers,
    includedFields,
  };
  const isClearButtonDisabled = !isFilterApplied && !existSimpleFilter;
  const expression = expressSearchQuery(config, currentQuery.rules, currentQuery.combinator, 0, selectedTab === TABS.CONFIGURATION);
  const fields = selectedTab === TABS.CONFIGURATION ? configurationNodes : getFields(config);

  useEffect(() => {
    if (isHiddenChargerConfiguration) {
      dispatch(loadAdvancedSearchPresets(filterId));
    }

    if (plazaAvailabilityMode === 'full' || plazaAvailabilityMode === 'partially') {
      dispatch(loadPlazasList(dictionariesTypesEnum.plazas));
    }

    dispatch(loadAllDictionaries());

    if (userPermissions.includes(permissions.SUPERVISOR)) {
      dispatch(loadDictionary(dictionariesTypesEnum.customers));
    }

    isEqual(appliedQuery, emptyQuery) && dispatch(setSelectedTabAdvancedSearch(TABS.CHARGER, id));

    document.addEventListener('keydown', onKeyDown, false);

    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, []);

  useEffect(() => {
    dispatch(loadAdvancedSearchPresets(filterId));
  }, [dispatch, filterId]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getConfigurationConfig();

      setConfigurationNodes(data);
    };

    isNodeConfigurationParametersAvailable && fetchData();

    return () => {
      if (!isEqual(appliedQuery, query)) {
        dispatch(changeFilterQuery(id, appliedQuery));
      }
    };
  }, []);

  useEffect(() => () => !isOpen && isEqual(appliedQuery, emptyQuery) && dispatch(setSelectedTabAdvancedSearch(TABS.CHARGER, id)), [isOpen]);

  return (
    <div className="advanced-search">
      {isShowAdvancedSearch && (
        <ButtonGroup variant="contained" color="secondary" style={buttonsStyle || {}}>
          <Button variant="contained" onClick={onFilterButtonClick} disableTouchRipple>
            {filterButtonLabel}
          </Button>
          {isFilterApplied && (
            <Button variant="contained" style={filterIconStyle}>
              <TooltipWrapper hideHighlight position="bottom" arrow tip={expression && <span>{expression}</span>}>
                <FilterList className="no-text" />
              </TooltipWrapper>
            </Button>
          )}
          {!isClearButtonDisabled && (
            <Button variant="contained" onClick={onClear}>
              Clear Filter
            </Button>
          )}
        </ButtonGroup>
      )}
      <Dialog
        className={`advanced-search-dialog ${isOnlyMobile ? 'mobile' : ''}`}
        actions={renderDialogFooter()}
        open={isOpen}
        onClose={onClose}
        contentClassName="advanced-search-modal"
        maxWidth={false}
        actionsStyle={dialogActionsStyle}
        paperProps={{
          style: paperStyle,
        }}
        disablePortal
      >
        {!isHiddenChargerConfiguration && (
          <Tabs
            value={selectedTab}
            className="advanced-search-dialog__tab"
            onChange={(_, tab) => changeTabHandler(tab)}
            bgColor="rgba(39, 94, 99, 0.1)"
          >
            <Tab label="Charger Parameters" value={TABS.CHARGER} />
            <Tab label="Configuration Parameters" value={TABS.CONFIGURATION} />
          </Tabs>
        )}
        <div className="advanced-search-query-bilder-container">
          <QueryBuilder
            fields={fields}
            query={currentQuery}
            onChange={onChange}
            configurationBuilder={selectedTab === TABS.CONFIGURATION}
          />
        </div>
        <div className="search-query-expression">
          <SearchQueryExpression expression={expression} />
          {renderPresets()}
        </div>
      </Dialog>
      <ConfirmationDialog
        onSubmit={onWarningSubmit}
        onCancel={closeWarningDialog}
        submitLabel="Proceed"
        cancelLabel="Cancel"
        open={isWarningOpen}
        bodyText={
          <div className="advanced-search-warning-message">Switching to Advanced Filter will discard the currently active filter!</div>
        }
      />
      <ConfirmationDialog
        onSubmit={proceedSwitchTabHandler}
        onCancel={() => setSwitchTabDialogOpened(false)}
        submitLabel="Proceed"
        cancelLabel="Cancel"
        open={switchTabDialogOpened}
        bodyText={<div className="advanced-search-warning-message">Switching between tabs will discard currently active filter.</div>}
      />
      <ConfirmationDialog
        onSubmit={proceedCloseDialogHandler}
        onCancel={() => setCloseDialogOpened(false)}
        submitLabel="Proceed"
        cancelLabel="Cancel"
        open={closeDialogOpened}
        bodyText={
          <div className="advanced-search-warning-message">
            You have unapplied filter conditions. Do you want to leave and discard your changes?
          </div>
        }
      />
      <SearchPresetDialog
        open={createPresetDialogOpened}
        submitPresetDialog={() => dispatch(submit('search-preset'))}
        closePresetDialog={closeCreatePresetDialog}
        addNewPreset={createPreset}
      />
      <SearchPresetDeleteDialog open={deletePresetDialogOpened} submit={deletePreset} cancel={closeDeletePresetDialog} />
    </div>
  );
};

export default AdvancedSearch;
